import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import type { Role } from "@/types/db";
import { hasPermission, type PermissionKey } from "@/lib/permissions";
import { canEnter } from "@/lib/route-policy";

interface Props {
  children: React.ReactNode;
  allow?: Role[];
  permission?: PermissionKey;
  require?: "support";
}

export function ProtectedRoute({ children, allow, permission, require }: Props) {
  const { session, employee, loading } = useAuth();
  const loc = useLocation();

  if (loading) {
    return <div className="flex h-screen items-center justify-center text-gray2">Loading…</div>;
  }
  if (!session) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (!employee) return <Navigate to="/welcome" replace />;
  if (employee.status !== "active") return <Navigate to="/deactivated" replace />;
  if (employee.role === "kiosk" && loc.pathname !== "/kiosk") return <Navigate to="/kiosk" replace />;
  if (
    !employee.onboarding_completed_at
    && employee.role !== "admin" && employee.role !== "super_admin"
    && employee.role !== "kiosk" && employee.role !== "investor" && employee.role !== "c_suite"
    && loc.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" replace />;
  }
  // HQ-ROUTE-ROLE-001. Routes that declare nothing used to inherit only the
  // checks above — active, not a kiosk — which admitted every other role
  // including investor. src/lib/route-policy.ts now states who each route is
  // for, and a test fails when a route is added without saying.
  if (!canEnter(loc.pathname, employee.role)) return <AccessDenied />;

  // A denial is a page, not a redirect. Redirecting to "/" hides which of the
  // two happened — no access, or the page not existing — and loops for a role
  // that cannot open "/" either.
  if (allow && !allow.includes(employee.role)) return <AccessDenied />;
  if (permission && !hasPermission(employee.role, employee.extra_permissions ?? null, permission)) {
    return <AccessDenied />;
  }
  if (require === "support") {
    const allowed = employee.can_access_support || employee.role === "admin" || employee.role === "super_admin";
    if (!allowed) return <AccessDenied />;
  }
  return <>{children}</>;
}

/**
 * What a role that cannot open a page is told.
 *
 * Deliberately says nothing about what is behind it. "You do not have access to
 * X" confirms X exists and roughly what it is, which is information the person
 * being refused has not earned.
 */
function AccessDenied() {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1 className="text-lg font-semibold">You don't have access to this page</h1>
      <p className="mt-2 text-sm text-gray2">
        If you think you should, ask an admin — they can grant it from your employee record.
      </p>
      <Link to="/" className="btn btn-ghost mt-5 inline-block">Back to dashboard</Link>
    </div>
  );
}
